import React, { useState, useRef, useEffect } from 'react';
import { UploadCloud, X, Trash2, FileText as FilePdf, Loader2 } from 'lucide-react';
import DynamicModal from '../../../components/DynamicModal';
import PdfPreviewModal from '../../../components/PdfPreviewModal';
import { uploadFileAttachment, deleteFileAttachment, getFileAttachmentByProjectId, getFileAttachmentByFilename } from '../../../services/projectReportServices';
import { PROJECT_REPORT_BASE_URL } from '../../../services/api';

const getErrorMessage = (error) => {
  if (error.response) {
    if (typeof error.response.data === 'string') {
      const preMatch = error.response.data.match(/<pre>(.*?)<br>/i);
      if (preMatch && preMatch[1]) {
        return preMatch[1].replace(/&nbsp;/g, ' ').trim();
      }
      return `Server Error (${error.response.status})`;
    }
    return error.response.data?.message || `Server Error (${error.response.status})`;
  } else if (error.request) {
    return "Koneksi terputus dari server";
  }
  return error.message || "Terjadi kesalahan tidak diketahui";
};

const DocumentUpload = ({ accentColor = '#14b8a6', onFilesChange, projectId, nik, categoryFile, sectionName, permissions, defaultAllowedDoc, pimproNik }) => {
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [pendingFiles, setPendingFiles] = useState([]);
  const [selectedPdf, setSelectedPdf] = useState(null);
  const [modalConfig, setModalConfig] = useState({ isOpen: false, type: '', file: null });
  const [isDragOver, setIsDragOver] = useState(false);

  const [isUploading, setIsUploading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isFetching, setIsFetching] = useState(false);
  const [previewLoadingId, setPreviewLoadingId] = useState(null);

  const fileInputRef = useRef(null);
  const canUpload = pimproNik ? nik === String(pimproNik).split('-')[0]
    : defaultAllowedDoc ? permissions.includes('project_report:edit')
      : false; //mengecek apakah nik current user adalah pimpro dan cek apakah user saat ini memiliki permission edit

  const fetchDocuments = async () => {
    if (!projectId) return;
    setIsFetching(true);
    try {
      const data = await getFileAttachmentByProjectId(projectId, categoryFile);
      const formattedFiles = data.map(item => ({
        id: item.id,
        name: item.filename,
        originalData: item
      }));
      setUploadedFiles(formattedFiles);
    } catch (error) {
      console.error("Failed to fetch documents", error);
    } finally {
      setIsFetching(false);
    }
  };

  const handlePreviewClick = async (file) => {
    if (!permissions.includes('project_report.cost_doc:read') && !defaultAllowedDoc && !canUpload) {
      setModalConfig({ isOpen: true, type: 'alert', message: 'Anda tidak memiliki izin untuk membaca atau melihat dokumen.' });
      return;
    }

    if (file.url) {
      setSelectedPdf(file);
      return;
    }

    setPreviewLoadingId(file.id);
    try {
      const data = await getFileAttachmentByFilename(file.id, file.name);
      if (data && data.filepath) {
        const url = `${PROJECT_REPORT_BASE_URL}${data.filepath.startsWith('/') ? data.filepath.slice(1) : data.filepath}`;
        const updatedFile = { ...file, url };
        setUploadedFiles(prev => prev.map(f => f.id === file.id ? updatedFile : f));
        setSelectedPdf(updatedFile);
      } else {
        throw new Error("Filepath tidak ditemukan dari server");
      }
    } catch (error) {
      console.error("Failed to fetch file URL", error);
      setModalConfig({
        isOpen: true,
        type: 'error',
        message: 'Gagal membuka dokumen:',
        failedFiles: [{ name: file.name, reason: getErrorMessage(error) }]
      });
    } finally {
      setPreviewLoadingId(null);
    }
  };

  useEffect(() => {
    fetchDocuments();
  }, [projectId]);



  useEffect(() => {
    if (onFilesChange) {
      onFilesChange(uploadedFiles.length > 0);
    }
  }, [uploadedFiles.length, onFilesChange]);

  const handleDragOver = (e) => {
    e.preventDefault();
    if (!permissions.includes('project_report.cost_doc:upload') && !canUpload) return;
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);
    if (!permissions.includes('project_report.cost_doc:upload') && !canUpload) {
      setModalConfig({ isOpen: true, type: 'alert', message: 'Anda tidak memiliki izin untuk mengunggah dokumen.' });
      return;
    }
    const files = Array.from(e.dataTransfer.files);
    handleFiles(files);
  };

  const handleUploadClick = () => {
    if (!permissions.includes('project_report.cost_doc:upload') && !canUpload) {
      setModalConfig({ isOpen: true, type: 'alert', message: 'Anda tidak memiliki izin untuk mengunggah dokumen.' });
      return;
    }
    fileInputRef.current?.click();
  };

  const handleFileInput = (e) => {
    const files = Array.from(e.target.files);
    handleFiles(files);
  };

  const handleFiles = (files) => {
    const pdfs = files.filter(file => file.type === 'application/pdf');
    if (pdfs.length !== files.length) {
      setModalConfig({ isOpen: true, type: 'alert', file: null });
      if (fileInputRef.current) fileInputRef.current.value = null;
      return;
    }

    if (pdfs.length > 0) {
      const newPending = pdfs.map(file => ({
        file,
        customName: file.name.replace(/\.[^/.]+$/, "")
      }));
      setPendingFiles(newPending);
      setModalConfig({ isOpen: true, type: 'rename', file: null });
    }

    if (fileInputRef.current) {
      fileInputRef.current.value = null;
    }
  };

  const confirmDelete = async () => {
    if (modalConfig.file) {
      setIsDeleting(true);
      const fileName = modalConfig.file.name;
      try {
        await deleteFileAttachment(modalConfig.file.id, nik);
        await fetchDocuments();
        if (selectedPdf?.id === modalConfig.file.id) {
          setSelectedPdf(null);
        }
        setIsDeleting(false);
        setModalConfig({ isOpen: true, type: 'success', message: 'Dokumen berhasil dihapus.' });
      } catch (error) {
        console.error("Failed to delete file", error);
        setIsDeleting(false);
        setModalConfig({
          isOpen: true,
          type: 'error',
          message: 'Gagal menghapus dokumen:',
          failedFiles: [{ name: fileName, reason: getErrorMessage(error) }]
        });
      }
    }
  };

  const confirmRename = async () => {
    setIsUploading(true);
    let successCount = 0;
    let failedFiles = [];

    for (const pf of pendingFiles) {
      let finalName = pf.customName.trim() || pf.file.name.replace(/\.[^/.]+$/, "");
      if (!finalName.toLowerCase().endsWith('.pdf')) {
        finalName += '.pdf';
      }

      const renamedFile = new File([pf.file], finalName, {
        type: pf.file.type,
        lastModified: pf.file.lastModified,
      });

      const formData = new FormData();
      formData.append('files', renamedFile);
      formData.append('projectId', projectId);
      formData.append('nik', nik);
      formData.append('categoryFile', categoryFile);

      try {
        await uploadFileAttachment(formData);
        successCount++;
      } catch (error) {
        console.error(`Failed to upload file: ${finalName}`, error);
        failedFiles.push({ name: finalName, reason: getErrorMessage(error) });
      }
    }

    await fetchDocuments();
    setIsUploading(false);
    setPendingFiles([]);

    if (failedFiles.length === 0 && successCount > 0) {
      setModalConfig({ isOpen: true, type: 'success', message: 'Dokumen berhasil diupload.' });
    } else if (failedFiles.length > 0) {
      setModalConfig({
        isOpen: true,
        type: 'error',
        message: 'Dokumen gagal diupload:',
        failedFiles: failedFiles
      });
    } else {
      setModalConfig({ isOpen: false, type: '', file: null });
    }
  };

  const closeModal = () => {
    if (isUploading || isDeleting) return;
    setModalConfig({ isOpen: false, type: '', file: null });
    if (modalConfig.type === 'rename') {
      setPendingFiles([]);
    }
  };

  return (
    <>
      <div
        className="mt-6 bg-white rounded-[20px] p-6 md:p-8 shadow-[0_4px_24px_rgba(0,0,0,0.06)] border border-slate-100"
      >
        <h2 className="text-[18px] font-bold text-slate-800 mb-6 flex items-center gap-2">
          {sectionName}
          {isFetching && <Loader2 size={16} className="animate-spin text-slate-400 ml-2" />}
        </h2>

        {/* PDF Upload Section */}
        <div className="xl:flex xl:flex-row xl:gap-4 space-y-4 xl:space-y-0">
          <div className="flex-1">
            <div
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onClick={handleUploadClick}
              className={`h-full min-h-[160px] border-2 border-dashed rounded-xl p-7 flex flex-col items-center justify-center cursor-pointer transition-all duration-200 ${isDragOver ? 'scale-[1.02]' : 'scale-100'}`}
              style={{
                borderColor: isDragOver ? accentColor : `${accentColor}60`,
                backgroundColor: isDragOver ? `${accentColor}15` : `${accentColor}05`
              }}
            >
              <UploadCloud size={44} style={{ color: accentColor }} className="mb-3" />
              <p className="text-lg font-semibold text-slate-700 mb-1">Drag & Drop</p>
              <p className="text-sm text-slate-500">or <span style={{ color: accentColor, fontWeight: '600' }}>Choose File</span></p>
              <input
                type="file"
                className="hidden"
                accept="application/pdf"
                multiple
                ref={fileInputRef}
                onChange={handleFileInput}
              />
            </div>
          </div>

          {/* Uploaded files list */}
          {uploadedFiles.length > 0 && (
            <div className="flex-1 flex flex-col">
              <h3 className="text-sm font-bold text-slate-700 mb-3 flex items-center gap-2">
                <FilePdf size={16} className="text-red-500" />
                Dokumen Terupload ({uploadedFiles.length})
              </h3>
              <div className="flex flex-col gap-3 max-h-[220px] overflow-y-auto pr-2" style={{ scrollbarWidth: 'thin' }}>
                {uploadedFiles.map(file => (
                  <div
                    key={file.id}
                    onClick={() => handlePreviewClick(file)}
                    className="flex items-center justify-between p-3 bg-white border border-slate-200 rounded-xl shadow-sm hover:border-blue-400 hover:shadow-md transition-all cursor-pointer group"
                  >
                    <div className="flex items-center gap-3 overflow-hidden">
                      <div className="p-2 bg-red-50 rounded-lg shrink-0 group-hover:bg-red-100 transition-colors">
                        <FilePdf size={20} className="text-red-500" />
                      </div>
                      <div className="flex flex-col overflow-hidden">
                        <span className="text-sm font-medium text-slate-700 truncate group-hover:text-blue-700 transition-colors" title={file.name}>{file.name}</span>
                        <span className="text-xs text-slate-500">PDF Document</span>
                      </div>
                    </div>
                    {/* <div className="flex items-center gap-1 shrink-0 ml-2">
                      {previewLoadingId === file.id && (
                        <Loader2 size={16} className="text-blue-500 animate-spin mr-2" />
                      )}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          if (!permissions.includes('project_report.cost_doc:upload') && !canUpload) {
                            setModalConfig({ isOpen: true, type: 'alert', message: 'Anda tidak memiliki izin untuk menghapus dokumen.' });
                            return;
                          }
                          setModalConfig({ isOpen: true, type: 'delete', file });
                        }}
                        className="p-2 rounded-lg bg-red-50 text-red-600 hover:bg-red-100 transition-all"
                        title="Hapus PDF"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div> */}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ── Dynamic Modal (Delete / Alert / Rename / Success / Error) ── */}
      <DynamicModal
        isOpen={modalConfig.isOpen}
        type={modalConfig.type}
        title={
          modalConfig.type === 'delete' ? 'Hapus Dokumen?' :
            modalConfig.type === 'rename' ? 'Beri Nama Dokumen' :
              modalConfig.type === 'success' ? 'Upload Berhasil' :
                modalConfig.type === 'error' ? 'Gagal Upload' : 'Peringatan'
        }
        message={modalConfig.message}
        file={modalConfig.file}
        failedFiles={modalConfig.failedFiles}
        onClose={closeModal}
        onConfirm={modalConfig.type === 'rename' ? confirmRename : confirmDelete}
        isLoading={modalConfig.type === 'rename' ? isUploading : isDeleting}
        confirmText={modalConfig.type === 'rename' ? 'Simpan' : 'Ya, Hapus'}
      >
        {modalConfig.type === 'rename' && (
          <div className="flex flex-col gap-4 mb-6 mt-4">
            {pendingFiles.map((pf, index) => (
              <div key={index} className="flex flex-col gap-1.5">
                <label className="text-xs font-semibold text-slate-500 truncate" title={pf.file.name}>
                  File asli: {pf.file.name}
                </label>
                <div className="relative">
                  <input
                    type="text"
                    value={pf.customName}
                    disabled={isUploading}
                    onChange={(e) => {
                      const updated = [...pendingFiles];
                      updated[index].customName = e.target.value;
                      setPendingFiles(updated);
                    }}
                    className="w-full pl-3 pr-10 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:border-blue-400 focus:bg-white focus:ring-2 focus:ring-blue-100 text-sm font-medium text-slate-700 transition-all disabled:opacity-70 disabled:cursor-not-allowed"
                    placeholder="Masukkan nama dokumen..."
                    autoFocus={index === 0}
                  />
                  <div className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-bold text-slate-400 pointer-events-none">
                    .pdf
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </DynamicModal>

      <PdfPreviewModal
        isOpen={!!selectedPdf}
        selectedFile={selectedPdf}
        files={uploadedFiles}
        onSelectFile={handlePreviewClick}
        onClose={() => setSelectedPdf(null)}
      />
    </>
  );
};

export default DocumentUpload;
